import React, { useState, useEffect, useRef, useCallback } from "react";
import { isEntityOn } from "./haConstants";
import { haProxyBlob } from "./haProxy";

/* ══ SMART HOME CAMERAS ═══════════════════════════════════
   Camera grid with auto-refreshing snapshots.
   Tapping a camera opens a larger view with MJPEG stream.

   HA camera API (direct from browser to HA instance):
   - Snapshot: GET /api/camera_proxy/{entity_id}
   - MJPEG:   GET /api/camera_proxy_stream/{entity_id}
   Both require Authorization: Bearer <token>               */

const SNAPSHOT_INTERVAL = 10000; /* refresh every 10s */

function SmartHomeCameras({ cameras, locId, haUrl, token, isRTL }) {
  const [activeCamera, setActiveCamera] = useState(null);

  if (!cameras.length) return (
    <div className="sh-scenes-empty">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--g300)" strokeWidth="1.3">
        <rect x="4" y="10" width="32" height="20" rx="3"/><circle cx="20" cy="20" r="6"/><path d="M12 10l2-4h12l2 4"/>
      </svg>
      <p>{isRTL ? "لا توجد كاميرات في Home Assistant" : "No cameras found in Home Assistant."}</p>
    </div>
  );

  if (activeCamera) return (
    <CameraLiveView
      entity={activeCamera}
      locId={locId}
      haUrl={haUrl}
      token={token}
      isRTL={isRTL}
      onBack={() => setActiveCamera(null)}
    />
  );

  return (
    <div className="sh-cameras">
      <div className="sh-cam-grid">
        {cameras.map(cam => (
          <CameraCard
            key={cam.entity_id}
            entity={cam}
            locId={locId}
            isRTL={isRTL}
            onClick={() => setActiveCamera(cam)}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Camera card with auto-refreshing snapshot ── */
function CameraCard({ entity, locId, isRTL, onClick }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [error, setError] = useState(false);
  const timerRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const blob = await haProxyBlob({
        locId,
        path: `/api/camera_proxy/${entity.entity_id}`,
      });
      const url = URL.createObjectURL(blob);
      setImgSrc(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      setError(false);
    } catch { setError(true); }
  }, [locId, entity.entity_id]);

  useEffect(() => {
    fetchSnapshot();
    timerRef.current = setInterval(fetchSnapshot, SNAPSHOT_INTERVAL);
    return () => {
      clearInterval(timerRef.current);
      if (imgSrc) URL.revokeObjectURL(imgSrc);
    };
  }, [fetchSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  const on = isEntityOn(entity) || entity.state !== "unavailable";
  const name = entity.friendly_name || entity.entity_id;

  return (
    <div className={`sh-cam-card${on ? "" : " sh-cam-offline"}`} onClick={on ? onClick : undefined}>
      <div className="sh-cam-preview">
        {imgSrc && !error ? (
          <img src={imgSrc} alt={name} className="sh-cam-img" />
        ) : error ? (
          <div className="sh-cam-placeholder">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--g400)" strokeWidth="1.3"><rect x="2" y="6" width="20" height="14" rx="2"/><circle cx="12" cy="13" r="4"/><path d="M8 6l2-3h4l2 3"/></svg>
            <span>{isRTL ? "غير متاح" : "Unavailable"}</span>
          </div>
        ) : (
          <div className="sh-cam-placeholder">
            <span>{isRTL ? "جارٍ التحميل..." : "Loading..."}</span>
          </div>
        )}
        {on && <div className="sh-cam-live-dot" />}
      </div>
      <div className="sh-cam-info">
        <span className="sh-cam-name">{name}</span>
        <span className="sh-cam-state">{on ? (isRTL ? "مباشر" : "Live") : (isRTL ? "غير متصل" : "Offline")}</span>
      </div>
    </div>
  );
}

/* ── Full-screen live view ── */
function CameraLiveView({ entity, locId, haUrl, token, isRTL, onBack }) {
  /* Default to snapshot mode (via proxy, always works). MJPEG stream is
     opt-in and only works when HA is reachable directly from the browser
     with the token embedded somehow — often fails silently and falls back. */
  const [mode, setMode] = useState("snapshot");
  const [snapshotSrc, setSnapshotSrc] = useState(null);
  const name = entity.friendly_name || entity.entity_id;

  /* MJPEG stream URL — rendered directly as img src.
     Uses the direct haUrl because <img> can't carry an Authorization
     header. Works if HA allows anonymous proxy streams; falls back to
     snapshot mode on error. */
  const streamUrl = haUrl ? `${haUrl}/api/camera_proxy_stream/${entity.entity_id}` : null;

  /* Snapshot fetch via backend proxy — no CORS, works from any HA setup. */
  const fetchSnapshot = useCallback(async () => {
    try {
      const blob = await haProxyBlob({
        locId,
        path: `/api/camera_proxy/${entity.entity_id}`,
      });
      const url = URL.createObjectURL(blob);
      setSnapshotSrc(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { /* ignore */ }
  }, [locId, entity.entity_id]);

  /* Auto-refresh snapshot in snapshot mode */
  useEffect(() => {
    if (mode !== "snapshot") return;
    fetchSnapshot();
    const t = setInterval(fetchSnapshot, 3000);
    return () => { clearInterval(t); };
  }, [mode, fetchSnapshot]);

  /* Cleanup */
  useEffect(() => () => { if (snapshotSrc) URL.revokeObjectURL(snapshotSrc); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="sh-cam-live">
      <div className="sh-room-header">
        <button className="sh-room-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d={isRTL ? "M6 3l5 5-5 5" : "M10 3L5 8l5 5"}/></svg>
        </button>
        <div style={{flex:1}}>
          <h2 className="sh-room-title">{name}</h2>
          <p className="sh-room-sub">{isRTL ? "بث مباشر" : "Live Feed"}</p>
        </div>
        <div className="sh-cam-mode-btns">
          <button className={`sh-mode-pill${mode==="snapshot"?" sh-mode-active":""}`} onClick={()=>setMode("snapshot")}>
            {isRTL?"صورة":"Snapshot"}
          </button>
          {streamUrl&&(
            <button className={`sh-mode-pill${mode==="stream"?" sh-mode-active":""}`} onClick={()=>setMode("stream")} title={isRTL?"تجريبي":"Experimental"}>
              {isRTL?"بث":"Stream"}
            </button>
          )}
        </div>
      </div>

      <div className="sh-cam-live-frame">
        {mode === "stream" ? (
          /* MJPEG stream — try direct img src first */
          <img
            src={streamUrl}
            alt={name}
            className="sh-cam-live-img"
            onError={() => setMode("snapshot")} /* fallback to snapshot if MJPEG fails (CORS) */
          />
        ) : (
          snapshotSrc ? (
            <img src={snapshotSrc} alt={name} className="sh-cam-live-img" />
          ) : (
            <div className="sh-cam-placeholder" style={{height:300}}>
              <span>{isRTL ? "جارٍ التحميل..." : "Loading..."}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default SmartHomeCameras;
